import { db, PREVIEW_DB_PREFIX, type PreviewFrame } from "@innoclouds/db";

const createMemoryStorage = (): Storage => {
	const items = new Map<string, string>();
	return {
		get length() {
			return items.size;
		},
		key: (index) => [...items.keys()][index] ?? null,
		getItem: (key) => items.get(key) ?? null,
		setItem: (key, value) => void items.set(key, String(value)),
		removeItem: (key) => void items.delete(key),
		clear: () => items.clear(),
	};
};

const createScopedStorage = (backend: Storage, prefix: string): Storage => {
	const keys = () => {
		const found: string[] = [];
		for (let i = 0; i < backend.length; i++) {
			const key = backend.key(i);
			if (key?.startsWith(prefix)) found.push(key.slice(prefix.length));
		}
		return found;
	};

	return {
		get length() {
			return keys().length;
		},
		key: (index) => keys()[index] ?? null,
		getItem: (key) => backend.getItem(prefix + key),
		setItem: (key, value) => backend.setItem(prefix + key, String(value)),
		removeItem: (key) => backend.removeItem(prefix + key),
		clear: () => {
			for (const key of keys()) backend.removeItem(prefix + key);
		},
	};
};

const usableSessionStorage = (): Storage | null => {
	try {
		const storage = window.sessionStorage;
		const probe = "inno-preview:probe";
		storage.setItem(probe, probe);
		storage.removeItem(probe);
		return storage;
	} catch {
		return null;
	}
};

const isReload = () => (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type === "reload";

export const isolateStorage = ({ scope, session, tile }: PreviewFrame) => {
	const backend = usableSessionStorage();
	const area = (name: string) => (backend ? createScopedStorage(backend, `inno-preview:${scope}:${name}:`) : createMemoryStorage());
	const local = area("local");
	const sessionArea = area("session");
	const sessionKey = `inno-preview:${scope}:session`;

	const isStale = (tile !== null && !isReload()) || (session !== null && backend?.getItem(sessionKey) !== session);
	if (isStale) {
		local.clear();
		sessionArea.clear();
	}
	if (session) backend?.setItem(sessionKey, session);

	Object.defineProperty(window, "localStorage", { configurable: true, get: () => local });
	Object.defineProperty(window, "sessionStorage", { configurable: true, get: () => sessionArea });
};

export const isolateMockStorage = () => {
	const local = createScopedStorage(window.localStorage, "inno-mock:local:");
	const session = createScopedStorage(window.sessionStorage, "inno-mock:session:");
	Object.defineProperty(window, "localStorage", { configurable: true, get: () => local });
	Object.defineProperty(window, "sessionStorage", { configurable: true, get: () => session });
};

const lockOf = (database: string) => `${PREVIEW_DB_PREFIX}-lock:${database}`;

const isPreviewDatabase = (name: string | undefined): name is string =>
	!!name && name !== db.name && (name === PREVIEW_DB_PREFIX || name.startsWith(`${PREVIEW_DB_PREFIX}:`));

const deleteDatabase = (name: string) =>
	new Promise<void>((resolve) => {
		const request = indexedDB.deleteDatabase(name);
		request.onsuccess = request.onerror = request.onblocked = () => resolve();
	});

export const releaseStaleDatabases = async () => {
	const locks = navigator.locks;
	if (!locks || typeof indexedDB?.databases !== "function") return;
	void locks.request(lockOf(db.name), { mode: "shared" }, () => new Promise<never>(() => {}));
	const stale = (await indexedDB.databases()).map(({ name }) => name).filter(isPreviewDatabase);
	for (const name of stale) await locks.request(lockOf(name), { ifAvailable: true }, (lock) => (lock ? deleteDatabase(name) : undefined));
};
